import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import StorageIcon from '@mui/icons-material/Storage';
import { PageContainer, Section, StatGrid } from '../components/AdminContentWrapper';
import IcebergCatalogService from '../../../services/IcebergCatalogService';

const cellSx = { color: 'var(--wb-text)', borderColor: 'var(--wb-border)' };
const headSx = { ...cellSx, color: 'var(--wb-muted-85)', fontWeight: 600 };
const mutedSx = { color: 'var(--wb-muted-75)' };

// Estados que devuelve GET /iceberg-maintenance (report.py::table_state).
const STATES = {
  maintained: { label: 'Mantenida', color: 'var(--wb-x-text-2dd4bf, #2dd4bf)', bg: 'var(--wb-x-bg-2dd4bf26, rgba(45,212,191,0.15))',
    help: 'Se expiraron snapshots y se borraron archivos que ya no se usaban.' },
  ok: { label: 'Al día', color: 'var(--wb-success)', bg: 'var(--wb-x-bg-4ade8026, rgba(74,222,128,0.15))',
    help: 'Verificada: no hay snapshots que expirar según la política.' },
  unchanged: { label: 'Sin cambios', color: 'var(--wb-muted)', bg: 'var(--wb-x-bg-94a3b826, rgba(148,163,184,0.15))',
    help: 'Sin escrituras desde la última verificación; no requiere mantenimiento.' },
  queued: { label: 'En cola', color: 'var(--wb-blue)', bg: 'var(--wb-x-bg-60a5fa26, rgba(96,165,250,0.15))',
    help: 'Cambió desde la última verificación; se revisará en los próximos minutos.' },
  pending: { label: 'Pendiente', color: 'var(--wb-warning)', bg: 'var(--wb-x-bg-fbbf2426, rgba(251,191,36,0.15))',
    help: 'Tabla nueva: se revisará en la próxima ejecución programada.' },
  failed: { label: 'Error', color: 'var(--wb-danger)', bg: 'var(--wb-x-bg-f8717126, rgba(248,113,113,0.15))',
    help: 'La última verificación falló; se reintentará en la próxima ejecución.' }
};

const HISTORY_LABELS = { vacuumed: 'Mantenimiento', not_eligible: 'Verificación', failed: 'Error' };

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
};

const StateChip = ({ state }) => {
  const meta = STATES[state] || STATES.pending;
  return (
    <Tooltip title={meta.help}>
      <Chip size="small" label={meta.label} sx={{ backgroundColor: meta.bg, color: meta.color, fontWeight: 600 }} />
    </Tooltip>
  );
};

const TableRowWithHistory = ({ table }) => {
  const [open, setOpen] = useState(false);
  const hasDetail = (table.history || []).length > 0 || table.last_error;
  return (
    <Fragment>
      <TableRow hover sx={{ '&:hover': { backgroundColor: 'var(--wb-x-bg-0ea5e90f, rgba(14,165,233,0.06))' } }}>
        <TableCell sx={cellSx} padding="checkbox">
          {hasDetail && (
            <IconButton size="small" onClick={() => setOpen(!open)} sx={{ color: 'var(--wb-muted)' }}
              aria-label={open ? 'Ocultar historial' : 'Ver historial'}>
              {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
            </IconButton>
          )}
        </TableCell>
        <TableCell sx={{ ...cellSx, fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}>{table.table_name}</TableCell>
        <TableCell sx={cellSx}><StateChip state={table.state} /></TableCell>
        <TableCell sx={cellSx}>{formatDate(table.last_modified_at)}</TableCell>
        <TableCell sx={cellSx} align="right">{table.snapshot_count ?? '—'}</TableCell>
        <TableCell sx={cellSx}>{formatDate(table.oldest_snapshot_at)}</TableCell>
        <TableCell sx={cellSx}>{formatDate(table.last_check_at)}</TableCell>
        <TableCell sx={cellSx}>
          {table.last_vacuum_at ? (
            <>
              {formatDate(table.last_vacuum_at)}
              {table.snapshots_expired != null && (
                <Typography component="span" sx={{ ...mutedSx, fontSize: '0.75rem', ml: 0.5 }}>
                  ({table.snapshots_expired} expirados)
                </Typography>
              )}
            </>
          ) : '—'}
        </TableCell>
      </TableRow>
      {hasDetail && (
        <TableRow>
          <TableCell sx={{ ...cellSx, py: 0 }} colSpan={8}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ my: 1.5 }}>
                {table.last_error && (
                  <Alert severity="error" sx={{ mb: 1.5 }}>{table.last_error}</Alert>
                )}
                <Typography sx={{ ...mutedSx, fontSize: '0.8rem', mb: 0.5 }}>Historial (últimos 10 eventos)</Typography>
                {(table.history || []).map((h, i) => (
                  <Typography key={i} sx={{ color: 'var(--wb-x-text-cbd5e1, #cbd5e1)', fontSize: '0.8rem' }}>
                    {formatDate(h.at)} · <strong>{HISTORY_LABELS[h.event] || h.event}</strong> · {h.detail}
                  </Typography>
                ))}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  );
};

const SchemaTables = ({ schema }) => (
  <Box sx={{ mb: 2.5 }}>
    <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1 }}>
      <Typography sx={{ color: 'var(--wb-text)', fontWeight: 600 }}>Esquema: {schema.name}</Typography>
      <Typography sx={{ ...mutedSx, fontSize: '0.8rem' }}>
        {schema.tables.length} {schema.tables.length === 1 ? 'tabla' : 'tablas'}
      </Typography>
    </Stack>
    {schema.tables.length === 0 ? (
      <Typography sx={{ ...mutedSx, fontSize: '0.85rem' }}>Este esquema no tiene tablas.</Typography>
    ) : (
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headSx} padding="checkbox" />
              <TableCell sx={headSx}>Tabla</TableCell>
              <TableCell sx={headSx}>Estado</TableCell>
              <TableCell sx={headSx}>Última modificación</TableCell>
              <TableCell sx={headSx} align="right">Snapshots</TableCell>
              <TableCell sx={headSx}>Snapshot más antiguo</TableCell>
              <TableCell sx={headSx}>Última verificación</TableCell>
              <TableCell sx={headSx}>Último mantenimiento</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {schema.tables.map((t) => <TableRowWithHistory key={t.table_name} table={t} />)}
          </TableBody>
        </Table>
      </TableContainer>
    )}
  </Box>
);

/**
 * Informe del mantenimiento automático de las tablas Iceberg del tenant
 * (GET /iceberg-maintenance, whisperbi-back-semantic-model). El backend toma
 * el tenant SOLO del token, así que cada usuario ve únicamente las tablas de
 * su cuenta; requiere catalog.iceberg.view, igual que el Catálogo Iceberg.
 */
const IcebergMaintenance = () => {
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState(null);

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      setReport(await IcebergCatalogService.getMaintenanceReport());
    } catch (err) {
      const message = err?.message || '';
      setError(message.startsWith('HTTP 403')
        ? 'No tienes permiso para ver el catálogo Iceberg (catalog.iceberg.view). Pide acceso al administrador de tu cuenta.'
        : message || 'No fue posible obtener el informe de mantenimiento.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Filtro por nombre de tabla y estado, conservando Base de Datos -> Esquema.
  const databases = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (report?.databases || [])
      .map((db) => ({
        ...db,
        schemas: db.schemas
          .map((schema) => ({
            ...schema,
            tables: schema.tables.filter((t) =>
              (!term || t.table_name.toLowerCase().includes(term)) && (!stateFilter || t.state === stateFilter))
          }))
          .filter((schema) => schema.tables.length > 0 || (!term && !stateFilter))
      }))
      .filter((db) => db.schemas.length > 0);
  }, [report, search, stateFilter]);

  const summary = report?.summary || {};
  const policy = report?.policy;

  return (
    <PageContainer
      title="Mantenimiento de tablas"
      description="Mantenimiento automático de las tablas Iceberg de tu cuenta: cada semana se revisan las tablas que cambiaron y, si tienen versiones (snapshots) más viejas que la política de retención, se expiran y se borran los archivos que ya no se usan."
      actions={
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={isLoading}
          sx={{ borderColor: 'var(--wb-blue-40)', color: 'var(--wb-blue)', '&:hover': { borderColor: 'var(--wb-blue)' } }}>
          Actualizar
        </Button>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Error al cargar el informe</AlertTitle>
          {error}
        </Alert>
      )}

      {isLoading && !report && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} sx={{ color: 'var(--wb-primary)' }} />
        </Box>
      )}

      {report && (
        <>
          <Box sx={{ mb: 3 }}>
            <StatGrid items={[
              { label: 'Tablas', value: summary.tables || 0 },
              { label: 'Al día / sin cambios', value: (summary.ok || 0) + (summary.unchanged || 0) },
              { label: 'Mantenidas', value: summary.maintained || 0 },
              { label: 'Pendientes / en cola / con error', value: (summary.pending || 0) + (summary.queued || 0) + (summary.failed || 0) }
            ]} />
          </Box>

          <Section title="Política y programación">
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 1, md: 4 }}>
              <Typography sx={{ color: 'var(--wb-text)' }}>
                <Box component="span" sx={mutedSx}>Retención: </Box>
                {policy?.retention_days} días, mínimo {policy?.min_snapshots_to_keep} snapshots por tabla
              </Typography>
              <Typography sx={{ color: 'var(--wb-text)' }}>
                <Box component="span" sx={mutedSx}>Frecuencia: </Box>{policy?.schedule}
              </Typography>
              <Typography sx={{ color: 'var(--wb-text)' }}>
                <Box component="span" sx={mutedSx}>Última ejecución: </Box>{formatDate(report.last_run_at)}
              </Typography>
              <Typography sx={{ color: 'var(--wb-text)' }}>
                <Box component="span" sx={mutedSx}>Próxima: </Box>{formatDate(report.next_run_at)}
              </Typography>
            </Stack>
          </Section>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
            <TextField
              size="small"
              placeholder="Buscar tabla"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 240, '& .MuiOutlinedInput-root': { color: 'var(--wb-text-strong)', '& fieldset': { borderColor: 'var(--wb-outline)' } } }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'var(--wb-muted)' }} /></InputAdornment> }}
            />
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
              {Object.entries(STATES).map(([key, meta]) => (
                <Chip
                  key={key}
                  label={`${meta.label} (${summary[key] || 0})`}
                  onClick={() => setStateFilter(stateFilter === key ? null : key)}
                  variant={stateFilter === key ? 'filled' : 'outlined'}
                  sx={{
                    color: meta.color,
                    borderColor: meta.color,
                    backgroundColor: stateFilter === key ? meta.bg : 'transparent'
                  }}
                />
              ))}
            </Stack>
          </Stack>

          {databases.length === 0 ? (
            <Section title="Sin tablas">
              <Typography sx={mutedSx}>
                {report.databases.length === 0
                  ? 'Tu cuenta todavía no tiene tablas en el catálogo Iceberg.'
                  : 'Ninguna tabla coincide con los filtros.'}
              </Typography>
            </Section>
          ) : databases.map((db) => (
            <Accordion
              key={db.iceberg_database_id}
              defaultExpanded
              disableGutters
              sx={{ backgroundColor: 'var(--wb-bg-panel)', color: 'var(--wb-text)', border: '1px solid var(--wb-border)', mb: 2, '&:before': { display: 'none' } }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'var(--wb-muted)' }} />}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <StorageIcon sx={{ color: 'var(--wb-blue)' }} />
                  <Typography sx={{ fontWeight: 600 }}>Base de Datos: {db.name}</Typography>
                  <Typography sx={{ ...mutedSx, fontSize: '0.8rem' }}>
                    {db.schemas.length} {db.schemas.length === 1 ? 'esquema' : 'esquemas'}
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                {db.schemas.map((schema) => <SchemaTables key={schema.iceberg_schema_id} schema={schema} />)}
              </AccordionDetails>
            </Accordion>
          ))}
        </>
      )}
    </PageContainer>
  );
};

export default IcebergMaintenance;
